/**
 * AdMob on iOS/Android through `@capacitor-community/admob` (v8), loaded from the
 * native entry chunk (NativeLoaders.admob).
 *
 * - init() starts the Mobile Ads SDK and re-applies the request configuration when
 *   the COPPA tags change (child-directed + under-age-of-consent, rating G). Test
 *   devices are registered only when `testing` is on.
 * - Personalisation: every request carries `npa` unless consent allows personalised
 *   ads, the account is not a child, the server's AdStartResponse.personalized agrees
 *   and, on iOS, App Tracking Transparency is authorised. requestTracking() shows the
 *   ATT prompt only once our own consent already allows personalised ads (SY-02.2).
 * - Google UMP: gatherConsent() shows Google's consent form where it is required, and
 *   ads are requested only while UMP says canRequestAds; umpCmp() plugs it into the
 *   Cmp seam. The first ad of a launch waits for UMP (and asks it itself when the game
 *   has not), so no request ever goes out before Google's certified CMP had its say. It
 *   runs at the first ad rather than at startup because the game asks the age question
 *   (SY-02.1) before its first ad, and UMP needs the answer (tagForUnderAgeOfConsent).
 * - Rewarded + SSV: the /v1/ads/reward/start nonce is `ssv.customData` and the player
 *   id `ssv.userId`. Google calls GET /v1/ads/admob/ssv with both (custom_data,
 *   user_id) and the API marks the nonce verified, so the game retries
 *   /v1/ads/reward/complete on 409 ad_not_verified for a few seconds. The plugin's
 *   show() promise settles only on a reward, so a view ends on the dismiss (or
 *   failed-to-show) event; every listener is removed after each show.
 */
import type { Cmp } from '../consent/cmp.js';
import type { ConsentChoice } from '../consent/store.js';
import type { Loader } from '../lazy.js';
import { nonPersonalized, type AdError, type AdSlot, type AdsInitOptions, type AdsProvider, type AdViewOptions, type RewardedResult } from './types.js';

interface Handle {
  remove(): Promise<void>;
}
export interface AdMobError {
  code: number;
  message: string;
}
export interface AdMobRewardItem {
  type: string;
  amount: number;
}
/** ATTrackingManager.AuthorizationStatus (Android and old iOS report `authorized`). */
export type TrackingStatus = 'authorized' | 'denied' | 'notDetermined' | 'restricted';
export type UmpStatus = 'NOT_REQUIRED' | 'OBTAINED' | 'REQUIRED' | 'UNKNOWN';
export interface UmpInfo {
  status: UmpStatus;
  isConsentFormAvailable?: boolean;
  canRequestAds: boolean;
  privacyOptionsRequirementStatus: 'NOT_REQUIRED' | 'REQUIRED' | 'UNKNOWN';
}

/** Event names of the plugin's RewardAdPluginEvents / InterstitialAdPluginEvents enums. */
export const REWARD_EVENTS = {
  failedToLoad: 'onRewardedVideoAdFailedToLoad',
  failedToShow: 'onRewardedVideoAdFailedToShow',
  rewarded: 'onRewardedVideoAdReward',
  dismissed: 'onRewardedVideoAdDismissed',
} as const;
export const INTERSTITIAL_EVENTS = {
  failedToShow: 'interstitialAdFailedToShow',
  dismissed: 'interstitialAdDismissed',
} as const;

/**
 * The part of the plugin used here, as in its definitions.d.ts (the test suite checks
 * that the real module is assignable to AdMobModule). Enum-typed options are widened
 * to their string values.
 */
export interface AdMobPlugin {
  initialize(o?: { testingDevices?: string[]; initializeForTesting?: boolean; tagForChildDirectedTreatment?: boolean; tagForUnderAgeOfConsent?: boolean; maxAdContentRating?: string }): Promise<void>;
  trackingAuthorizationStatus(): Promise<{ status: TrackingStatus }>;
  requestTrackingAuthorization(): Promise<void>;
  requestConsentInfo(o?: { debugGeography?: number; testDeviceIdentifiers?: string[]; tagForUnderAgeOfConsent?: boolean }): Promise<UmpInfo>;
  showConsentForm(): Promise<UmpInfo>;
  showPrivacyOptionsForm(): Promise<void>;
  prepareRewardVideoAd(o: { adId: string; isTesting?: boolean; npa?: boolean; ssv?: { userId?: string; customData?: string } }): Promise<{ adUnitId: string }>;
  showRewardVideoAd(): Promise<AdMobRewardItem>;
  prepareInterstitial(o: { adId: string; isTesting?: boolean; npa?: boolean }): Promise<{ adUnitId: string }>;
  showInterstitial(): Promise<void>;
  addListener(event: 'onRewardedVideoAdFailedToLoad', fn: (e: AdMobError) => void): Promise<Handle>;
  addListener(event: 'onRewardedVideoAdFailedToShow', fn: (e: AdMobError) => void): Promise<Handle>;
  addListener(event: 'onRewardedVideoAdReward', fn: (r: AdMobRewardItem) => void): Promise<Handle>;
  addListener(event: 'onRewardedVideoAdDismissed', fn: () => void): Promise<Handle>;
  addListener(event: 'interstitialAdFailedToShow', fn: (e: AdMobError) => void): Promise<Handle>;
  addListener(event: 'interstitialAdDismissed', fn: () => void): Promise<Handle>;
}
export interface AdMobModule {
  AdMob: AdMobPlugin;
}

/** Google's published sample ad units: they always fill with test ads (developers.google.com/admob/{android,ios}/test-ads). */
export const ADMOB_TEST_UNITS = {
  ios: { rewarded: 'ca-app-pub-3940256099942544/1712485313', interstitial: 'ca-app-pub-3940256099942544/4411468910' },
  android: { rewarded: 'ca-app-pub-3940256099942544/5224354917', interstitial: 'ca-app-pub-3940256099942544/1033173712' },
} as const;

export interface AdMobConfig {
  /** ca-app-pub-…/… for this platform */
  rewardedAdUnit: string;
  interstitialAdUnit: string;
  /** the player id, sent as the SSV `user_id`: the API verifies a nonce only for its owner */
  userId?: () => string | null;
  /** ATT exists on iOS only */
  platform?: 'ios' | 'android';
  /** test ads and test devices; never in store builds */
  testing?: boolean;
  /** device ids registered as test devices (and UMP test devices) when `testing` */
  testingDevices?: string[];
  /** UMP debug geography when `testing`: 1 = EEA, 3 = regulated US state */
  umpDebugGeography?: number;
  load: Loader<AdMobModule>;
}

/** What the UMP flow ended with. */
export interface UmpResult {
  status: UmpStatus;
  canRequestAds: boolean;
  formShown: boolean;
  /** show a "Privacy options" entry in settings (showPrivacyOptions) */
  privacyOptionsRequired: boolean;
}

/** Android LoadAdError codes 3 (no fill) and 9 (mediation no fill); iOS reports code 0 with "No ad to show". */
export const isNoFill = (e: AdMobError | null): boolean => e !== null && (e.code === 3 || e.code === 9 || /no.?fill|no ad to show/i.test(e.message));

const messageOf = (e: unknown): string => String((e as { message?: unknown } | null)?.message ?? e);

function deferred<T>(): { promise: Promise<T>; resolve: (v: T) => void } {
  let resolve!: (v: T) => void;
  const promise = new Promise<T>((r) => (resolve = r));
  return { promise, resolve };
}

/** Listener handles of one ad view, removed together. */
class Subscriptions {
  private readonly handles: Promise<Handle>[] = [];
  add(h: Promise<Handle>): Promise<Handle> {
    this.handles.push(h);
    return h;
  }
  async removeAll(): Promise<void> {
    await Promise.all(this.handles.splice(0).map(async (h) => (await h).remove()));
  }
}

export class AdMobProvider implements AdsProvider {
  readonly network = 'admob';
  private opts: AdsInitOptions = { personalized: false, childDirected: true };
  private sdk: AdMobPlugin | null = null;
  /** childDirected value of the last initialize() call */
  private tagged: boolean | null = null;
  private att: TrackingStatus | null = null;
  private canRequest = true;
  private showing = false;
  /** this launch's UMP round for a COPPA state; cleared when it fails, so the next ad asks again */
  private ump: { child: boolean; done: Promise<UmpResult> } | null = null;

  constructor(private readonly c: AdMobConfig) {}

  async init(opts: AdsInitOptions): Promise<void> {
    this.opts = opts;
    const { AdMob } = await this.c.load();
    if (this.tagged !== opts.childDirected) {
      await AdMob.initialize({
        ...(this.c.testing ? { initializeForTesting: true, testingDevices: this.c.testingDevices ?? [] } : {}),
        tagForChildDirectedTreatment: opts.childDirected,
        tagForUnderAgeOfConsent: opts.childDirected,
        ...(opts.childDirected ? { maxAdContentRating: 'General' } : {}),
      });
      this.tagged = opts.childDirected;
    }
    if (this.c.platform === 'ios') this.att = (await AdMob.trackingAuthorizationStatus()).status;
    this.sdk = AdMob;
  }

  /** Rewarded ads cannot be preloaded (the SSV nonce is fixed at load time): ready = SDK up, UMP allows, a player to credit. */
  async isRewardedReady(_slot: AdSlot): Promise<boolean> {
    return this.sdk !== null && this.canRequest && !this.showing && Boolean(this.c.userId?.());
  }

  async showRewarded(_slot: AdSlot, nonce: string, view: AdViewOptions = {}): Promise<RewardedResult> {
    const fail = (error: AdError, detail?: string): RewardedResult => ({ completed: false, network: this.network, error, ...(detail ? { detail } : {}) });
    const userId = this.c.userId?.();
    if (!userId) return fail('failed', 'no player id for the SSV user_id');
    if (this.showing) return fail('failed', 'another ad is showing');
    const ad = this.plugin();
    this.showing = true;
    const subs = new Subscriptions();
    try {
      if (!(await this.consented())) return fail('nofill', 'UMP: consent does not allow ad requests');
      const outcome = deferred<RewardedResult>();
      const seen: { loadError: AdMobError | null; reward: AdMobRewardItem | null } = { loadError: null, reward: null };
      await Promise.all([
        subs.add(ad.addListener(REWARD_EVENTS.failedToLoad, (e) => (seen.loadError = e))),
        subs.add(ad.addListener(REWARD_EVENTS.rewarded, (r) => (seen.reward = r))),
        subs.add(ad.addListener(REWARD_EVENTS.dismissed, () => outcome.resolve(seen.reward ? this.earned(seen.reward) : fail('dismissed')))),
        subs.add(ad.addListener(REWARD_EVENTS.failedToShow, (e) => outcome.resolve(fail('failed', e.message)))),
      ]);
      const loaded = await ad.prepareRewardVideoAd({ adId: this.c.rewardedAdUnit, isTesting: this.c.testing === true, npa: this.npa(view), ssv: { customData: nonce, userId } }).then(
        () => true,
        (e: unknown) => {
          seen.loadError ??= { code: -1, message: messageOf(e) };
          return false;
        },
      );
      if (!loaded) return fail(isNoFill(seen.loadError) ? 'nofill' : 'failed', seen.loadError?.message);
      // resolves only when the reward is earned; a skip ends with the dismissed event instead
      void ad.showRewardVideoAd().then(
        (r) => (seen.reward ??= r),
        (e: unknown) => outcome.resolve(fail('failed', messageOf(e))),
      );
      return await outcome.promise;
    } finally {
      this.showing = false;
      await subs.removeAll();
    }
  }

  /** Resolves once the ad is closed (true) or could not be shown (false). */
  async showInterstitial(): Promise<{ shown: boolean }> {
    if (this.showing) return { shown: false };
    const ad = this.plugin();
    this.showing = true;
    const subs = new Subscriptions();
    try {
      if (!(await this.consented())) return { shown: false };
      const closed = deferred<boolean>();
      await Promise.all([
        subs.add(ad.addListener(INTERSTITIAL_EVENTS.dismissed, () => closed.resolve(true))),
        subs.add(ad.addListener(INTERSTITIAL_EVENTS.failedToShow, () => closed.resolve(false))),
      ]);
      const opts = { adId: this.c.interstitialAdUnit, isTesting: this.c.testing === true, npa: this.npa({}) };
      if (!(await ad.prepareInterstitial(opts).then(() => true, () => false))) return { shown: false };
      if (!(await ad.showInterstitial().then(() => true, () => false))) return { shown: false };
      return { shown: await closed.promise };
    } finally {
      this.showing = false;
      await subs.removeAll();
    }
  }

  /**
   * SY-02.2 ATT prompt (the game calls it after level 5). Asks only while our consent
   * allows personalised ads for a non-child account; a refusal makes every later
   * request non-personalised. Returns the status, or null off iOS.
   */
  async requestTracking(): Promise<TrackingStatus | null> {
    if (this.c.platform !== 'ios') return null;
    const { AdMob } = await this.c.load();
    let { status } = await AdMob.trackingAuthorizationStatus();
    if (status === 'notDetermined' && !nonPersonalized(this.opts)) {
      await AdMob.requestTrackingAuthorization();
      ({ status } = await AdMob.trackingAuthorizationStatus());
    }
    this.att = status;
    return status;
  }

  /**
   * Google UMP: refresh the consent info and show Google's form where it is required
   * (EEA/UK, regulated US states). Call before init() on first run and on every launch.
   */
  gatherConsent(o: { childDirected: boolean }): Promise<UmpResult> {
    const done = (async () => {
      const { AdMob } = await this.c.load();
      let info = await AdMob.requestConsentInfo({
        tagForUnderAgeOfConsent: o.childDirected,
        ...(this.c.testing ? { testDeviceIdentifiers: this.c.testingDevices ?? [], ...(this.c.umpDebugGeography !== undefined ? { debugGeography: this.c.umpDebugGeography } : {}) } : {}),
      });
      const formShown = info.status === 'REQUIRED' && info.isConsentFormAvailable === true;
      if (formShown) info = await AdMob.showConsentForm();
      return this.adoptUmp(info, formShown);
    })();
    const round = (this.ump = { child: o.childDirected, done });
    done.catch(() => {
      if (this.ump === round) this.ump = null;
    });
    return done;
  }

  /** May this ad be requested: UMP asked this launch for the current COPPA state (asked now if not), and it allows requests. */
  private consented(): Promise<boolean> {
    const round = this.ump?.child === this.opts.childDirected ? this.ump : null;
    return (round?.done ?? this.gatherConsent({ childDirected: this.opts.childDirected })).then(
      () => this.canRequest,
      () => false, // UMP unreachable: no request this time, the next ad asks again
    );
  }

  /** The settings entry point UMP requires when privacyOptionsRequired. */
  async showPrivacyOptions(): Promise<UmpResult> {
    const { AdMob } = await this.c.load();
    await AdMob.showPrivacyOptionsForm();
    return this.adoptUmp(await AdMob.requestConsentInfo(), true);
  }

  private adoptUmp(info: UmpInfo, formShown: boolean): UmpResult {
    this.canRequest = info.canRequestAds;
    return { status: info.status, canRequestAds: info.canRequestAds, formShown, privacyOptionsRequired: info.privacyOptionsRequirementStatus === 'REQUIRED' };
  }

  private npa(view: AdViewOptions): boolean {
    return nonPersonalized(this.opts) || view.personalized === false || (this.c.platform === 'ios' && this.att !== 'authorized');
  }

  private earned(r: AdMobRewardItem): RewardedResult {
    return { completed: true, network: this.network, proof: JSON.stringify({ type: r.type, amount: r.amount }) };
  }

  private plugin(): AdMobPlugin {
    if (!this.sdk) throw new Error('AdMobProvider: call init() first');
    return this.sdk;
  }
}

/**
 * The Cmp seam over Google's UMP form. The plugin does not expose the TCF purposes, and
 * the Mobile Ads SDK applies them itself, so the stored record only narrows: ads follow
 * UMP's canRequestAds, personalised ads stay what the player chose in our own UI.
 */
export function umpCmp(ads: AdMobProvider, childDirected: () => boolean): Cmp {
  return {
    async prompt(_region, current): Promise<ConsentChoice> {
      const r = await ads.gatherConsent({ childDirected: childDirected() });
      if (r.status === 'NOT_REQUIRED') return current;
      return { analytics: current.analytics, ads: r.canRequestAds, personalizedAds: current.personalizedAds && r.canRequestAds };
    },
  };
}
